<script setup lang="ts">
/**
 * Поля релиза: репозиторий, версия, описание — макет 92:1400.
 *
 * Одна форма на два места: в карточке задачи она живёт в модалке
 * (ReleaseDialog), на экране раздела стоит колонкой справа. Логика у них одна,
 * и держать её в двух файлах значило бы чинить проверку версии дважды.
 *
 * Список репозиториев — подсказка: у карточки его отдаёт сервис по эпику, у
 * экрана он собирается из истории. Решает человек, поэтому поле остаётся
 * вводимым.
 *
 * Тег ставится на master: в проекте релиз — это тег на уже влитом коммите, и
 * возможность собрать релиз с произвольной ветки приглашала бы выкатить
 * непроверенное.
 */
import { AppButton, AppSelect, AppTextField } from '@vinprop/design-system'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { createRelease } from '../api'

const props = defineProps<{
  services: string[]
  issueId?: string
  issueKey?: string
}>()

const emit = defineEmits<{ done: []; failed: [error: unknown] }>()

const { t } = useI18n()

const projectPath = ref(props.services[0] ?? '')
const tag = ref('')
const description = ref('')
const busy = ref(false)
const tagField = ref<HTMLElement | null>(null)

// Подсказки приезжают асинхронно: у карточки — запросом по эпику, у экрана —
// вместе с историей. Первый пришедший вариант и подставляем.
watch(
  () => props.services,
  (services) => {
    if (!projectPath.value) projectPath.value = services[0] ?? ''
  },
)

const options = computed(() => props.services.map((path) => ({ value: path, label: path })))

/** Версия должна быть семвером с v: тег другого вида пайплайн релиза не ловит. */
const isValidTag = computed(() => /^v\d+\.\d+\.\d+$/.test(tag.value.trim()))

const submit = async () => {
  if (!projectPath.value || !isValidTag.value) return

  busy.value = true

  try {
    await createRelease(
      projectPath.value,
      tag.value.trim(),
      'master',
      description.value.trim() || props.issueKey || tag.value.trim(),
      props.issueId,
    )
    tag.value = ''
    description.value = ''
    emit('done')
  } catch (failure) {
    emit('failed', failure)
  } finally {
    busy.value = false
  }
}

// Кнопки бывают снаружи: в модалке они стоят в футере, а не в теле формы.
defineExpose({
  submit,
  busy,
  canSubmit: computed(() => Boolean(projectPath.value) && isValidTag.value),
  focus: () => tagField.value?.querySelector('input')?.focus(),
})
</script>

<template>
  <div class="rel">
    <p class="rel__hint">{{ t('devops.release.hint') }}</p>

    <AppSelect
      v-if="options.length"
      v-model="projectPath"
      :options="options"
      :label="t('devops.release.service')"
    />
    <AppTextField
      v-else
      v-model="projectPath"
      :label="t('devops.release.service')"
      placeholder="vinprop/backend/tracker"
    />

    <div ref="tagField">
      <AppTextField
        v-model="tag"
        :label="t('devops.release.tag')"
        placeholder="v1.4.0"
        :error="tag && !isValidTag ? t('devops.release.tagInvalid') : ''"
      />
    </div>

    <AppTextField
      v-model="description"
      :label="t('devops.release.description')"
      multiline
      :rows="3"
    />

    <slot name="actions" :submit="submit" :busy="busy" :disabled="!projectPath || !isValidTag">
      <AppButton :loading="busy" :disabled="!projectPath || !isValidTag" @click="submit">
        {{ t('devops.release.create') }}
      </AppButton>
    </slot>
  </div>
</template>

<style scoped>
.rel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rel__hint {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
}
</style>
